import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { LikeButton } from '@components/Likes/Button';
import { useLikePlaylist } from '@components/Likes/useLikePlaylist';
import { SongCard } from '@components/basics/SongCard';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { PlaylistCardActionFragmentDoc, PlaylistCardDetailsFragmentDoc } from '@graphql/reactQuery';
import { ShelfCreator } from '../ShelfCreator';
import { PlaylistReleaseCountText } from '../shared';
import { CoverImages } from './CoverImages';
import { PreviewDropdown } from './Dropdown';
import { PlaylistCardSocialProof } from './PlaylistCardSocialProof';
import { usePlaylistCardInfo } from './usePlaylistCardInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistCardDetails on Shelf {
    id
    __typename
    user {
      id
      ...ShelfCreator
    }
    likes
    releaseCount
    ...PlaylistCardAction
    ...PlaylistCardInfoHook
    ...PlaylistCoverImages
  }
`);

export const PlaylistCardSkeleton = () => {
  return (
    <SongCard
      isPlaying={false}
      album={false}
      edition={false}
      expanded={null}
      setExpanded={null}
      loading
    >
      <SongCard.Cover
        onPlayButtonClick={() => {
          return;
        }}
        image={null}
        currentListeningParty={null}
      />

      <SongCard.Info
        header={null}
        subheader={null}
        actions={null}
        // Since it will only render if there is something so empty fragment does the job
        infoContainerBottom={<></>}
      />
    </SongCard>
  );
};

export const PlaylistCard = ({
  data,
  showMetadata,
}: {
  data: FragmentType<PlaylistCardDetailsFragmentDoc>;
  showMetadata: boolean;
}) => {
  const playlist = getFragment(PlaylistCardDetailsFragmentDoc, data);
  const { isPlaying, onClick, isBufferingPlaylist, playlistPagePath, releaseCount, playlistName } =
    usePlaylistCardInfo({
      data: playlist,
    });

  return (
    <SongCard
      isPlaying={isPlaying}
      loading={false}
      album={false}
      edition={false}
      expanded={null}
      setExpanded={null}
    >
      <SongCard.Cover
        coverLink={playlistPagePath}
        onPlayButtonClick={onClick}
        isBufferingSong={isBufferingPlaylist}
        image={<CoverImages playlist={playlist} maxSize={343} priority={false} />}
        currentListeningParty={null}
      />

      <SongCard.Info
        header={<AccessibleLink pathnameUri={playlistPagePath}>{playlistName}</AccessibleLink>}
        subheader={<ShelfCreator data={playlist.user} showAvatar={false} />}
        actions={<PlaylistCardActions data={playlist} />}
        infoContainerBottom={
          showMetadata ? <PlaylistReleaseCountText releaseCount={releaseCount} /> : null
        }
        bottom={showMetadata && <PlaylistCardSocialProof playlistId={playlist.id} />}
      />
    </SongCard>
  );
};

gql(/* GraphQL */ `
  fragment PlaylistCardAction on Shelf {
    id
    trackIds
    type
    webappUri
    user {
      id
    }
    ...PlaylistPreviewDropdownInfo
  }
`);

const PlaylistCardActions = ({ data }: { data: FragmentType<PlaylistCardActionFragmentDoc> }) => {
  const playlist = getFragment(PlaylistCardActionFragmentDoc, data);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const { isLiked, onClick: onLikeClick } = useLikePlaylist({
    id: playlist.id,
    source: 'Card',
  });
  const { authUser } = useAuth();

  const isOwner = authUser?.id === playlist.user.id;
  const isUserCreated = playlist.type === 'DEFAULT';

  const showLike = isUserCreated && !isOwner;

  return (
    <div className="-mr-3 flex">
      <div className="[&_path]:transition-colors [&_path]:duration-300 [&_path]:hover:fill-white">
        <PreviewDropdown
          playlist={playlist}
          trackIds={playlist.trackIds}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          isOwner={isOwner}
          playlistType={playlist.type}
          playlistLink={playlist.webappUri}
          variant="tertiary"
          triggerProps={{}}
        />
      </div>
      {showLike && (
        <div className="[&_path]:transition-colors [&_path]:duration-300 [&_path]:hover:fill-white">
          <LikeButton onClick={onLikeClick} liked={isLiked} disabled={false} variant="tertiary" />
        </div>
      )}
    </div>
  );
};
